import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fileUrl, fileKind, formatSize, type MediaFile, fileToResizedDataUrl, DEFAULT_IMAGE_MAX_DIM, DEFAULT_IMAGE_QUALITY } from "../../utils/media";
import { iconExternal, iconReplace, iconX, iconDownload, iconPlay } from "../../utils/icons";

function extOf(filename?: string, mimeType?: string): string {
	if (filename && filename.includes(".")) {
		return filename.split(".").pop()!.slice(0, 4).toUpperCase();
	}
	const byMime: Record<string, string> = {
		"application/pdf": "PDF",
		"application/zip": "ZIP",
		"application/vnd.ms-excel": "XLS",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLS",
		"application/msword": "DOC",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOC",
	};
	if (mimeType && byMime[mimeType]) return byMime[mimeType];
	if (mimeType && mimeType.startsWith("image/")) return "IMG";
	if (mimeType && mimeType.startsWith("video/")) return "VID";
	if (mimeType && mimeType.startsWith("audio/")) return "AUD";
	return "FILE";
}

function colorFor(ext: string): string {
	const e = ext.toLowerCase();
	if (e === "pdf") return "#e53935";
	if (["doc", "docx", "rtf"].includes(e)) return "#2b7cd3";
	if (["xls", "xlsx", "csv"].includes(e)) return "#2e8b57";
	if (["ppt", "pptx"].includes(e)) return "#e8740c";
	if (["zip", "rar", "7z", "gz", "tar"].includes(e)) return "#7e57c2";
	if (["mp4", "mov", "avi", "mkv", "webm"].includes(e)) return "#7c4dff";
	if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(e)) return "#5a9d1e";
	return "#6d6e71";
}

interface Preview { url: string; kind: "image" | "video" | "file"; filename: string; ext: string; color: string; size: number; }

@customElement("clam-media-input")
export class ClamMediaInput extends LitElement {
	static styles = css`
		:host { display: block; font-family: var(--clam-font); }
		.mi { display: flex; flex-direction: column; gap: var(--clam-space-2, 8px); }

		/* === barra superior: tipo + contador + agregar === */
		.mi__bar { display: flex; align-items: center; gap: var(--clam-space-2, 8px); min-height: 34px; }
		.mi__meta { flex: 1; font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); }
		.mi__meta b { color: var(--clam-text, #424244); font-weight: 600; }
		.mi__add {
			display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; min-height: 34px;
			border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px);
			background: var(--clam-surface, #fff); color: var(--clam-text, #333); cursor: pointer;
			font-size: var(--clam-text-xs, 12px); font-weight: 600; font-family: var(--clam-font); white-space: nowrap;
		}
		.mi__add:hover { border-color: var(--clam-light-green, #6bbb24); color: var(--clam-green, #5a9d1e); background: rgba(107,187,36,0.05); }
		.mi__add svg { width: 13px; height: 13px; }

		/* === tile vacío (imagen única): cuadrado compacto === */
		.dz-square {
			width: 160px; height: 160px; box-sizing: border-box;
			border: 2px dashed var(--clam-border, #d8dee9); border-radius: var(--clam-radius-md, 10px);
			display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
			cursor: pointer; transition: border-color .15s, background .15s; background: var(--clam-surface-soft, #fafbfc);
		}
		.dz-square:hover, .dz-square.dragover { border-color: var(--clam-light-green, #6bbb24); background: rgba(107,187,36,0.06); }
		.dz-square .dz__icon { color: var(--clam-text-muted, #9ca3af); transition: color .15s; }
		.dz-square:hover .dz__icon, .dz-square.dragover .dz__icon { color: var(--clam-light-green, #6bbb24); }
		.dz__text { font-size: var(--clam-text-xs, 11px); color: var(--clam-text, #424244); font-weight: 600; }
		.dz__hint { font-size: 10px; color: var(--clam-light-gray, #9ca3af); }

		/* === dropzone vacío (archivos): ancho acotado === */
		.dz-compact {
			max-width: 460px; box-sizing: border-box; padding: 14px;
			border: 2px dashed var(--clam-border, #d8dee9); border-radius: var(--clam-radius-md, 10px);
			display: flex; align-items: center; gap: 10px; cursor: pointer; transition: border-color .15s, background .15s;
			background: var(--clam-surface-soft, #fafbfc);
		}
		.dz-compact:hover, .dz-compact.dragover { border-color: var(--clam-light-green, #6bbb24); background: rgba(107,187,36,0.06); }
		.dz-compact .dz__icon { color: var(--clam-text-muted, #9ca3af); }
		.dz-compact:hover .dz__icon, .dz-compact.dragover .dz__icon { color: var(--clam-light-green, #6bbb24); }

		/* === imagen única: preview grande con acciones overlay === */
		.single { position: relative; width: 180px; height: 180px; border-radius: var(--clam-radius-md, 10px); overflow: hidden; border: 1px solid var(--clam-border, #e2e8f0); box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
		.single__img { width: 100%; height: 100%; object-fit: cover; display: block; background: var(--clam-surface-soft, #f0f2f5); }
		.single__acts { position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; gap: 6px; padding: 8px; background: linear-gradient(to top, rgba(0,0,0,0.55), transparent 60%); opacity: 0; transition: opacity .15s; }
		.single:hover .single__acts, .single:focus-within .single__acts { opacity: 1; }
		@media (hover: none) { .single__acts { opacity: 1; } }
		.single__act {
			display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%;
			background: rgba(255,255,255,0.95); color: var(--clam-text, #333); border: none; cursor: pointer; text-decoration: none; font-size: 19px;
			box-shadow: 0 1px 4px rgba(0,0,0,0.2); transition: transform .12s, background .12s;
		}
		.single__act:hover { background: #fff; transform: scale(1.08); }
		.single__act--danger { color: var(--clam-danger, #ef4444); }
		.single__act--danger:hover { background: var(--clam-danger, #ef4444); color: #fff; }

		/* === galería: grid de thumbnails + tile "+" === */
		.gallery { display: flex; flex-wrap: wrap; gap: var(--clam-space-2, 8px); }
		.gallery.dragover { outline: 2px dashed var(--clam-light-green, #6bbb24); outline-offset: 4px; border-radius: var(--clam-radius-md, 10px); }
		.gal__thumb { position: relative; width: 132px; height: 132px; border-radius: var(--clam-radius-sm, 8px); overflow: hidden; border: 1px solid var(--clam-border, #e2e8f0); box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
		.gal__img { width: 100%; height: 100%; object-fit: cover; display: block; background: var(--clam-surface-soft, #f0f2f5); }
		.gal__acts { position: absolute; top: 4px; right: 4px; display: flex; gap: 4px; }
		.gal__act { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.62); color: #fff; border: none; cursor: pointer; text-decoration: none; font-size: 15px; box-shadow: 0 1px 3px rgba(0,0,0,0.25); transition: transform .12s, background .12s; }
		.gal__act:hover { background: rgba(0,0,0,0.85); transform: scale(1.1); }
		.gal__act--danger:hover { background: var(--clam-danger, #ef4444); }
		.gal__add {
			width: 132px; height: 132px; border: 2px dashed var(--clam-border, #d8dee9); border-radius: var(--clam-radius-sm, 8px);
			display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; cursor: pointer;
			color: var(--clam-text-muted, #9ca3af); background: var(--clam-surface-soft, #fafbfc); transition: border-color .15s, color .15s;
		}
		.gal__add:hover { border-color: var(--clam-light-green, #6bbb24); color: var(--clam-light-green, #6bbb24); }
		.gal__add svg { width: 22px; height: 22px; }
		.gal__add span { font-size: 10px; font-weight: 600; }

		/* === archivos: filas === */
		.files { display: flex; flex-direction: column; gap: var(--clam-space-2, 8px); max-width: 520px; }
		.file {
			display: flex; align-items: center; gap: var(--clam-space-2, 8px); width: 100%;
			border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 8px);
			padding: 8px 10px; background: var(--clam-surface, #fff); box-sizing: border-box;
		}
		.file:hover { border-color: var(--clam-border, #d0d8e8); box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
		.file__badge {
			width: 38px; height: 46px; border-radius: 5px; background: var(--c, #6d6e71); color: #fff;
			display: flex; align-items: flex-end; justify-content: center; padding-bottom: 5px;
			font-size: 9px; font-weight: 800; letter-spacing: 0.03em; position: relative; flex-shrink: 0; text-decoration: none;
		}
		.file__badge::after { content: ""; position: absolute; top: 0; right: 0; border-style: solid; border-width: 0 9px 9px 0; border-color: transparent rgba(255,255,255,0.4) transparent transparent; }
		.file__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
		.file__name { font-size: var(--clam-text-sm, 13px); font-weight: 600; color: var(--clam-text, #333); text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.file__name:hover { color: var(--clam-green, #5a9d1e); }
		.file__meta { font-size: 11px; color: var(--clam-text-muted, #9ca3af); }
		.acts { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
		.act {
			display: inline-flex; align-items: center; justify-content: center;
			width: 38px; height: 38px; border-radius: var(--clam-radius-sm, 6px);
			border: none; cursor: pointer; text-decoration: none; background: transparent; color: var(--clam-text-muted, #6d6e71); font-size: 18px;
			transition: background .12s, color .12s, transform .12s;
		}
		.act:hover { background: var(--clam-surface-soft, #f0f2f5); color: var(--clam-text, #333); transform: scale(1.06); }
		.act--danger:hover { background: rgba(239,68,68,0.12); color: var(--clam-danger, #ef4444); }
		.mi__warn { font-size: var(--clam-text-xs, 11px); color: var(--clam-danger, #ef4444); background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25); padding: 6px 10px; border-radius: var(--clam-radius-sm, 6px); margin-top: 6px; }
	`;

	@property() accept = "*/*";
	@property({ type: Boolean }) multi = false;
	@property({ type: Number }) maxSize = 0;
	@property({ type: Number }) maxItems = 50;
	/** Dimensión máxima (px) para imágenes: se redimensiona para evitar payloads enormes (413). */
	@property({ type: Number }) maxImageDim = DEFAULT_IMAGE_MAX_DIM;
	/** Dimensión mínima (px) para imágenes: las más chicas se rechazan. 0 = sin mínimo. */
	@property({ type: Number }) minImageDim = 0;
	@property({ attribute: false }) value: MediaFile | MediaFile[] | null = null;

	private _dragover = false;
	private _sizeWarn = "";

	private get isImageOnly(): boolean {
		return this.accept.startsWith("image/");
	}

	private get acceptLabel(): string {
		if (this.accept === "*/*" || !this.accept) return "Cualquier archivo";
		if (this.accept.startsWith("image/")) return "Imágenes";
		if (this.accept.startsWith("video/")) return "Videos";
		return this.accept;
	}

	private get previews(): Preview[] {
		const raw = Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
		// Filtra items fantasma (objeto vacío sin url ni _base64) para no renderizar una tarjeta vacía.
		const arr = raw.filter((r) => r && ((r as MediaFile).url || (r as MediaFile)._base64));
		return arr.map((item) => {
			const r = (item || {}) as MediaFile;
			const filename = r.filename || "";
			const ext = extOf(filename, r.mimeType);
			return {
				url: fileUrl(item),
				kind: fileKind(item, this.accept),
				filename,
				ext,
				color: colorFor(ext),
				size: r.size || 0,
			};
		});
	}

	private get count(): number {
		return Array.isArray(this.value) ? this.value.length : this.value ? 1 : 0;
	}

	private triggerInput() {
		(this.shadowRoot?.querySelector("#file-input") as HTMLInputElement)?.click();
	}

	private async processFiles(fileList: FileList | null) {
		if (!fileList || fileList.length === 0) return;
		const files = Array.from(fileList);
		// Filtro por tamaño de archivo (bytes) si maxSize está seteado.
		let valid = files;
		let rejected = 0;
		if (this.maxSize > 0) {
			valid = files.filter((f) => f.size <= this.maxSize);
			rejected = files.length - valid.length;
		}
		const dimRejected: string[] = [];

		const processed: MediaFile[] = [];
		for (const file of valid) {
			// Redimensiona/comprime imágenes (downscale a maxImageDim). No-imagenes pasan tal cual.
			const res = await fileToResizedDataUrl(file, this.maxImageDim, DEFAULT_IMAGE_QUALITY);
			// Mínimo de dimensión (solo imágenes).
			if (this.minImageDim > 0 && file.type.startsWith("image/") && res.width > 0 && (res.width < this.minImageDim || res.height < this.minImageDim)) {
				dimRejected.push(`${file.name} (${res.width}×${res.height})`);
				continue;
			}
			// Tamaño del base64 resultante (aprox: chars * 0.75).
			const resultBytes = res.dataUrl.length * 0.75;
			processed.push({
				_base64: res.dataUrl,
				filename: file.name,
				size: Math.round(resultBytes),
				mimeType: res.resized ? (file.type === "image/png" ? "image/png" : "image/jpeg") : file.type,
			});
		}
		const dimMsg = dimRejected.length
			? `${dimRejected.length} imagen(es) rechazada(s): menores a ${this.minImageDim}px (${dimRejected.join(", ")}).`
			: "";
		this._sizeWarn = [rejected > 0 ? `${rejected} archivo(s) rechazado(s): superan ${formatSize(this.maxSize)}.` : "", dimMsg].filter(Boolean).join(" ");

		if (processed.length === 0) { this.requestUpdate(); return; }

		if (this.multi) {
			const current = Array.isArray(this.value) ? [...this.value] : [];
			const combined = [...current, ...processed].slice(0, this.maxItems);
			this.dispatchEvent(new CustomEvent("change", { detail: combined }));
		} else {
			this.dispatchEvent(new CustomEvent("change", { detail: processed[0] }));
		}
		this.requestUpdate();
	}

	private fileToBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	private removeFile(index: number) {
		if (this.multi && Array.isArray(this.value)) {
			const arr = [...this.value];
			arr.splice(index, 1);
			this.dispatchEvent(new CustomEvent("change", { detail: arr.length > 0 ? arr : null }));
		} else {
			this.dispatchEvent(new CustomEvent("change", { detail: null }));
		}
		this.requestUpdate();
	}

	private onDrop(e: DragEvent) {
		e.preventDefault();
		this._dragover = false;
		this.requestUpdate();
		void this.processFiles(e.dataTransfer?.files ?? null);
	}

	private renderHeader() {
		const c = this.count;
		const sizeHint = this.maxSize ? ` · máx ${formatSize(this.maxSize)}` : "";
		let meta: unknown;
		if (this.isImageOnly) {
			meta = html`<b>${this.multi ? "Galería" : "Imagen"}</b> · ${c > 0 ? `${c} ${c === 1 ? "imagen" : "imágenes"}` : "sin imágenes"}${this.multi ? ` (tope ${this.maxItems})` : ""}`;
		} else {
			meta = html`<b>${this.acceptLabel}</b> · ${c > 0 ? `${c} ${c === 1 ? "archivo" : "archivos"}` : "sin archivos"}${sizeHint}`;
		}
		const canAdd = !this.multi ? c === 0 : c < this.maxItems;
		return html`
			<div class="mi__bar">
				<span class="mi__meta">${meta}</span>
				${canAdd
					? html`<button type="button" class="mi__add" @click=${() => this.triggerInput()}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
						${this.isImageOnly ? (this.multi ? "Agregar" : (c === 0 ? "Subir" : "Reemplazar")) : (c === 0 ? "Subir" : "Agregar")}
					</button>`
					: null}
			</div>
		`;
	}

	render() {
		const previews = this.previews;
		return html`
			<div class="mi"
				@dragover=${(e: DragEvent) => { e.preventDefault(); this._dragover = true; this.requestUpdate(); }}
				@dragleave=${() => { this._dragover = false; this.requestUpdate(); }}
				@drop=${(e: DragEvent) => this.onDrop(e)}
			>
			<input id="file-input" type="file" accept=${this.accept} ?multiple=${this.multi} hidden
				@change=${(e: Event) => void this.processFiles((e.target as HTMLInputElement).files)} />
			${this.renderHeader()}
			${this._sizeWarn ? html`<div class="mi__warn">${this._sizeWarn}</div>` : null}
			${this.isImageOnly
					? (this.multi ? this.renderGallery(previews) : this.renderSingleImage(previews[0]))
					: this.renderFiles(previews)}
			</div>
		`;
	}

	private renderSingleImage(p?: Preview) {
		if (!p || !p.url) {
			return html`
				<div class="dz-square ${this._dragover ? "dragover" : ""}" @click=${() => this.triggerInput()}>
					<span class="dz__icon">
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
					</span>
					<span class="dz__text">Subir imagen</span>
					<span class="dz__hint">${this.acceptLabel}</span>
				</div>
			`;
		}
		return html`
			<div class="single">
				<img class="single__img" src=${p.url} alt=${p.filename} />
				<div class="single__acts">
					<a class="single__act" href=${p.url} target="_blank" rel="noopener" title="Abrir" @click=${(e: Event) => e.stopPropagation()}>${iconExternal()}</a>
					<button class="single__act" title="Reemplazar" @click=${(e: Event) => { e.stopPropagation(); this.triggerInput(); }}>${iconReplace()}</button>
					<button class="single__act single__act--danger" title="Quitar" @click=${(e: Event) => { e.stopPropagation(); this.removeFile(0); }}>${iconX()}</button>
				</div>
			</div>
		`;
	}

	private renderGallery(previews: Preview[]) {
		return html`
			<div class="gallery ${this._dragover ? "dragover" : ""}">
				${previews.map((p, i) => html`
					<div class="gal__thumb">
						<img class="gal__img" src=${p.url} alt=${p.filename} />
						<div class="gal__acts">
							<a class="gal__act" href=${p.url} target="_blank" rel="noopener" title="Abrir" @click=${(e: Event) => e.stopPropagation()}>${iconExternal()}</a>
							<button class="gal__act gal__act--danger" title="Quitar" @click=${(e: Event) => { e.stopPropagation(); this.removeFile(i); }}>${iconX()}</button>
						</div>
					</div>
				`)}
				${this.count < this.maxItems
					? html`<button type="button" class="gal__add" title="Agregar imágenes" @click=${() => this.triggerInput()}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
						<span>Agregar</span>
					</button>`
					: null}
			</div>
		`;
	}

	private renderFiles(previews: Preview[]) {
		if (previews.length === 0) {
			return html`
				<div class="dz-compact ${this._dragover ? "dragover" : ""}" @click=${() => this.triggerInput()}>
					<span class="dz__icon">
						<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>
					</span>
					<span>
						<span class="dz__text">Arrastra o haz click</span><br/>
						<span class="dz__hint">${this.acceptLabel}${this.maxSize ? " · máx " + formatSize(this.maxSize) : ""}</span>
					</span>
				</div>
			`;
		}
		return html`<div class="files">${previews.map((p, i) => this.renderFile(p, i))}</div>`;
	}

	private renderFile(p: Preview, i: number) {
		const href = p.url || "#";
		const label = p.filename || (p.kind === "video" ? "Video" : "Archivo");
		const videoIcon = p.kind === "video" ? html`<span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);display:flex;color:#fff;">${iconPlay()}</span>` : null;
		return html`
			<div class="file">
				<a class="file__badge" style="--c:${p.color}" href=${href} target="_blank" rel="noopener" title="Abrir" @click=${(e: Event) => e.stopPropagation()}>
					${p.ext}${videoIcon}
				</a>
				<div class="file__info">
					<a class="file__name" href=${href} target="_blank" rel="noopener" title=${label} @click=${(e: Event) => e.stopPropagation()}>${label}</a>
					<span class="file__meta">${[p.size ? formatSize(p.size) : null, p.ext].filter(Boolean).join(" · ")}</span>
				</div>
				<div class="acts">
					<a class="act" href=${href} target="_blank" rel="noopener" download title="Abrir / descargar" @click=${(e: Event) => e.stopPropagation()}>${iconDownload()}</a>
					<button class="act act--danger" title="Quitar" @click=${(e: Event) => { e.stopPropagation(); this.removeFile(i); }}>${iconX()}</button>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"clam-media-input": ClamMediaInput;
	}
}
